'use client';

import { useEffect, useMemo, useState, useRef } from 'react';
import { useRewardsHubConfig, useCppConfig } from '@/hooks/use-rewards/use-rewards';
import { useCppEventsHandler } from '@/hooks/use-rewards/use-cpp-events-handler';
import { useGlobalContext } from '@/store/contexts/global-context';
import { isIosWrapper } from '@/logic/platform';
import {
    trackRewardsHubPromoEventToAmplitude,
    PromoApiEventType,
    type RewardsHubPromoEvent
} from '@/tracking/amplitude/events/rewards-hub/promo-events';
import {
    trackRewardsHubAmplitude,
    type RewardsHubSharedState,
    trackPromoOptInFailedToAmplitude,
    trackPromoOptInSuccessfulToAmplitude,
    type PromoOptInContext
} from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import {
    REWARDS_HUB_AMP_EVENT,
    REWARDS_MODULE,
    LOBBY_PAGE_NAME
} from '@/tracking/amplitude/events/rewards-hub/event-names';
import { toSnakeCase } from '@/tracking/amplitude/utils';
import { useRewardsPromoTracking } from '@/hooks/use-rewards/use-rewards-promo-tracking';
import { usePromotionLauncher } from '@/hooks/use-promotion-launcher/use-promotion-launcher.hook';
import { isPromoViewedInSession, markPromoViewedInSession } from '@/utils/rewards-hub/promo-progress-viewed-session';

const PROMO_API_EVENT_TYPES = new Set<string>(Object.values(PromoApiEventType));

export const usePromoCarouselConfig = () => {
    const sharedState = useRef<RewardsHubSharedState | null>(null);
    const [sharedStateLoaded, setSharedStateLoaded] = useState(false);
    const activeCardsRef = useRef(0);
    const { isLoggedIn } = useGlobalContext();

    const rewardsHubConfig = useRewardsHubConfig(undefined, isLoggedIn);
    const [promoConfig] = useCppConfig();
    const [eventsHandler, anchorCallback] = useCppEventsHandler();
    const eventsHandlerRef = useRef(eventsHandler);

    const { onPrimaryLinkClicked, onSecondaryLinkClicked, onCardClicked, sendEventRef } = useRewardsPromoTracking({
        sharedStateRef: sharedState
    });

    useEffect(() => {
        eventsHandlerRef.current = eventsHandler;
    }, [eventsHandler]);

    const handlers = useMemo(() => {
        const lobbyProps = {
            Module: REWARDS_MODULE.MERCH_SWIM_LANE,
            'Page Name': LOBBY_PAGE_NAME
        };

        const track = (event: string, props?: Record<string, unknown>) => {
            trackRewardsHubAmplitude({
                sendEvent: sendEventRef.current,
                event,
                sharedState: sharedState.current,
                props: {
                    ...lobbyProps,
                    ...props
                }
            });
        };

        return {
            handleSharedState: (props: RewardsHubSharedState) => {
                sharedState.current = props;
                setSharedStateLoaded((prev) => (prev ? prev : true));
            },

            handleActivePromoLoaded: (props: { activeCards: number }) => {
                activeCardsRef.current = props.activeCards;
            },

            handleActiveCardCTAClicked: (props: {
                promoId: string;
                promoName: string;
                linkText: string;
                promoProgress: number;
                activeCards?: number;
            }) =>
                track(REWARDS_HUB_AMP_EVENT.REWARDS_ACTIVE_CARD_CTA_CLICKED, {
                    'Promo ID': props.promoId,
                    'Promo Name': props.promoName,
                    'Link Text': toSnakeCase(props.linkText),
                    'Promo Progress': props.promoProgress,
                    'Active Cards': props.activeCards ?? activeCardsRef.current
                }),

            handlePromoCardViewed: (props: {
                position: number;
                promoName: string;
                promoId: string;
                interactionType: 'screen_view' | 'scroll';
                promoProgress: number;
            }) => {
                if (isPromoViewedInSession(props.promoId)) return;
                markPromoViewedInSession(props.promoId);

                track(REWARDS_HUB_AMP_EVENT.PROMO_PROGRESS_CARD_VIEWED, {
                    Position: props.position,
                    'Promo ID': props.promoId,
                    'Promo Name': props.promoName,
                    'Interaction Type': props.interactionType,
                    'Promo Progress': props.promoProgress
                });
            },

            handleNavigationLinkClicked: (props: { linkText: string; linkUrl: string }) =>
                track(REWARDS_HUB_AMP_EVENT.NAVIGATION_LINK_CLICKED, {
                    'Link Text': toSnakeCase(props.linkText),
                    'Link URL': props.linkUrl
                }),

            handlePromoOptInFailed: (props: PromoOptInContext) =>
                trackPromoOptInFailedToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: sharedState.current,
                    context: { ...props, location: 'carousel' }
                }),
            handlePromoOptInSuccessful: (props: PromoOptInContext) =>
                trackPromoOptInSuccessfulToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: sharedState.current,
                    context: { ...props, location: 'carousel' }
                })
        };
    }, [sendEventRef]);

    const wrappedApplication = isIosWrapper();
    const promotionLauncher = usePromotionLauncher();
    const promoEventsBridge = useMemo(
        () => (event: RewardsHubPromoEvent) => {
            const eventType = event?.type;

            // Opt-in API events must not emit a legacy Promo Opt In Submitted — the opt-in click is already
            // tracked as Promo Card Clicked via the primary-link click. Skip them here. See CASPROMO-17516.
            if (!eventType || !PROMO_API_EVENT_TYPES.has(eventType)) {
                trackRewardsHubPromoEventToAmplitude({
                    sendEvent: sendEventRef.current,
                    event,
                    sharedState: sharedState.current
                });
            }
            eventsHandlerRef.current?.(event);
        },
        [sendEventRef]
    );

    const promoHandlers = useMemo(
        () => ({
            ...promoConfig,
            onPrimaryLinkClicked,
            onSecondaryLinkClicked,
            onCardClicked,
            events: promoEventsBridge,
            anchorCallback,
            wrappedApplication,
            launcher: promotionLauncher
        }),
        [
            promoConfig,
            onPrimaryLinkClicked,
            onSecondaryLinkClicked,
            onCardClicked,
            promoEventsBridge,
            anchorCallback,
            wrappedApplication,
            promotionLauncher
        ]
    );

    return {
        config: rewardsHubConfig,
        handlers,
        promoHandlers,
        isSharedStateLoaded: sharedStateLoaded
    };
};
